import { z } from 'zod/v4';

/**
 * `GET /risk-analysis/risk-landscape/:countryCode` params, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk-landscape.js`'s
 * `get.params`.
 */
export const GetRiskLandscapeParamsSchema = z.object({
  countryCode: z.string().min(1).max(36),
});

export type GetRiskLandscapeParams = z.infer<
  typeof GetRiskLandscapeParamsSchema
>;

/**
 * One risk's `indexes` entry — a free-form JSON blob written by risk-gross
 * create/update (`title`, `indexValue`, `link`, `sourceType`, and optionally
 * `id`, `description`, `year`, `retracedScore`). Kept as a loosely-typed record
 * rather than an enumerated shape, same treatment as `companyData` in
 * `assessment.ts` — the legacy handler only ever spreads it back out unchanged
 * apart from rounding `indexValue`.
 */
export const RiskLandscapeIndexSchema = z.record(z.string(), z.unknown());

/**
 * One risk inside the landscape, ported from the `risks` column of
 * `getQuery` (`2025-06/risk-analysis-service/data/risk-landscape/queries/get.js`)
 * plus `buildIndexesData`'s per-risk `indexes` handling.
 *
 * `indexes` is `null` (not `[]`) both when `risk_gross.indexes` is SQL NULL and
 * when it parses to an empty array — the legacy handler only assigns a non-null
 * value inside its `if (indexesData.length)` branch.
 */
export const RiskLandscapeEntrySchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  grossValue: z.string(),
  indexes: z.array(RiskLandscapeIndexSchema).nullable(),
  description: z.string().nullable(),
});

/**
 * `GET /risk-analysis/risk-landscape/:countryCode` response, ported from
 * `get` in `2025-06/risk-analysis-service/handlers/v3/risk-landscape/get/index.js`.
 *
 * `facilityCount` and `netScore` are scoped to the caller's company; the rest of
 * the landscape is not scoped to any company — see `RiskLandscapeService`'s doc
 * comment.
 *
 * `netScore` is `null` when the caller has no READY standard assessment of a
 * linked, non-removed network company in that country. Legacy sent the gross
 * score instead (DELTA-2778).
 */
export const RiskLandscapeResourceSchema = z.object({
  countryCode: z.string(),
  countryName: z.string(),
  countryCodeAlpha3: z.string().nullable(),
  center: z.unknown(),
  grossScore: z.number(),
  netScore: z.number().nullable(),
  facilityCount: z.number(),
  risks: z.array(RiskLandscapeEntrySchema),
});

export type RiskLandscapeResource = z.infer<typeof RiskLandscapeResourceSchema>;

/**
 * `GET /risk-analysis/risk-landscape/companies` querystring, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk-landscape.js`'s
 * `listCompanies` export. `sort` and `filter` are unconstrained strings in
 * legacy (no regex/enum) — `sort` in particular accepts either a static column
 * or any risk title's camelCase form, which isn't known at schema-definition
 * time (see `buildRiskSortMapping`) — so both stay plain, length-capped strings
 * here rather than a stricter pattern.
 */
export const RiskLandscapeCompaniesListQuerySchema = z.strictObject({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(0).max(500).default(15),
  sort: z.string().max(512).optional(),
  filter: z.string().max(512).optional(),
});

export type RiskLandscapeCompaniesListQuery = z.infer<
  typeof RiskLandscapeCompaniesListQuerySchema
>;

/**
 * One risk entry inside a company row's `risks` array — distinct from
 * `RiskLandscapeEntrySchema`: `grossValue` here is the assessment's raw
 * `likelihood_score` (a number, possibly `null`), not the `get` endpoint's
 * `COALESCE(..., 'No Data')` text.
 */
export const RiskLandscapeCompanyRiskSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  riskGroup: z.string().nullable(),
  grossValue: z.number().nullable(),
});

export const RiskLandscapeCompanyProductTypeSchema = z.object({
  id: z.string(),
});

export const RiskLandscapeCompanyOrderVolumeSchema = z.object({
  value: z.number().nullable(),
  unit: z.string().nullable(),
  year: z.number().nullable(),
});

/**
 * One row of `GET /risk-analysis/risk-landscape/companies`, ported from
 * `buildCompaniesLandscapeData`
 * (`2025-06/risk-analysis-service/handlers/v3/risk-landscape/helpers.js`).
 */
export const RiskLandscapeCompanyRowSchema = z.object({
  companyId: z.string(),
  companyName: z.string(),
  country: z.string().nullable(),
  countryName: z.string().nullable(),
  facilityProcesses: z.array(z.string()).nullable(),
  companySize: z.string().nullable(),
  relationshipType: z.array(z.string()).nullable(),
  productTypes: z.array(RiskLandscapeCompanyProductTypeSchema).nullable(),
  annualPurchaseVolume: RiskLandscapeCompanyOrderVolumeSchema.nullable(),
  companyAverageLikelihoodScore: z.number().nullable(),
  companyAveragePriorityScore: z.number().nullable(),
  grossCountryAverageLikelihoodScore: z.number().nullable(),
  riskLevel: z.string().nullable(),
  risks: z.array(RiskLandscapeCompanyRiskSchema),
  featuredImageFileId: z.string().nullable(),
  featuredImageFileUrl: z.string().nullable(),
  minAverageCompanyPriorityScore: z.number().nullable(),
  maxAverageCompanyPriorityScore: z.number().nullable(),
});

export type RiskLandscapeCompanyRow = z.infer<
  typeof RiskLandscapeCompanyRowSchema
>;

/**
 * `GET /risk-analysis/risk-landscape/countries` querystring, ported from
 * `listCountries.querystring` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk-landscape.js`.
 * Default page size stays 15. `count` starts at 1 (legacy `0` silently became
 * 15). `sort` stays a plain string: besides `country`/`riskLevel`/`grossScore`
 * it accepts any risk title's camelCase form, which isn't known up front.
 * Unknown params are stripped, not rejected — legacy AJV did the same.
 */
export const RiskLandscapeCountriesListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(1).max(500).default(15),
  sort: z.string().max(512).optional(),
  filter: z.string().max(512).optional(),
});

export type RiskLandscapeCountriesListQuery = z.infer<
  typeof RiskLandscapeCountriesListQuerySchema
>;

/** One risk inside a country row. */
export const RiskLandscapeCountryRiskSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  /** `gross_value` as text (`'3.00'`), or `'No Data'` when it is NULL. */
  grossValue: z.string(),
  riskGroup: z.string().nullable(),
});

/**
 * One row of `GET /risk-analysis/risk-landscape/countries`.
 *
 * The fields both frontends read. Legacy also sent `center` and
 * `countryCodeAlpha3`, which neither reads; its response definition's
 * `netScore`/`facilityCount` were never selected at all.
 *
 * `riskLevel` is mixed case (`Low` … `Extreme`) as legacy emitted it, and is
 * `Extreme` for a country whose gross values are all NULL — the CASE's ELSE.
 */
export const RiskLandscapeCountryRowSchema = z.object({
  countryCode: z.string(),
  countryName: z.string().nullable(),
  grossScore: z.number().nullable(),
  riskLevel: z.string(),
  risks: z.array(RiskLandscapeCountryRiskSchema),
});

export type RiskLandscapeCountryRow = z.infer<
  typeof RiskLandscapeCountryRowSchema
>;

/**
 * `GET /risk-analysis/risk-landscape` querystring, ported from `list.querystring`
 * in `2025-06/risk-analysis-service/routes/schemas/v3/request/risk-landscape.js`.
 * Legacy declared `page`/`count` required but AJV filled their defaults first,
 * so they never were — both default here. `count` starts at 1 (legacy `0`
 * silently became 15). Unknown params are stripped, as AJV did.
 *
 * `filter` is stringified JSON: `countryCode` (comma-separated) and `scope`
 * (`"global"`, or anything else for the caller's network — the default).
 */
export const RiskLandscapeListQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce.number().int().min(1).max(500).default(15),
  sort: z.string().max(512).optional(),
  filter: z.string().max(512).optional(),
});

export type RiskLandscapeListQuery = z.infer<
  typeof RiskLandscapeListQuerySchema
>;

/**
 * One row of `GET /risk-analysis/risk-landscape` — a country's gross score
 * alongside the caller's network facility count and average net score there.
 * The legacy `newRiskLandscape` definition, field for field; `facilityCount`
 * and `netScore` are null where the caller has no assessed facilities.
 */
export const RiskLandscapeListRowSchema = z.object({
  countryCode: z.string(),
  countryName: z.string().nullable(),
  grossScore: z.number().nullable(),
  facilityCount: z.number().nullable(),
  netScore: z.number().nullable(),
});

export type RiskLandscapeListRow = z.infer<typeof RiskLandscapeListRowSchema>;

/**
 * `GET /risk-analysis/risk-landscape/map` querystring, ported from
 * `map.querystring` in
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/risk-landscape.js`.
 *
 * `filter` is stringified JSON: `country` or `countryCode` (comma-separated)
 * and `scope` (`"global"`, or anything else for the caller's network — the
 * default). Unknown params are stripped, as AJV did.
 */
export const RiskLandscapeMapQuerySchema = z.object({
  filter: z.string().max(512).optional(),
});

export type RiskLandscapeMapQuery = z.infer<typeof RiskLandscapeMapQuerySchema>;

/** One assessed company's marker: `center` is `[lng, lat]`. */
export const RiskLandscapeMapCompanySchema = z.object({
  companyName: z.string(),
  center: z.array(z.number()),
});

/**
 * One country of `GET /risk-analysis/risk-landscape/map` — the legacy
 * `landscapeMapData` definition, field for field. It never sent the country's
 * own `center`. `facilityCount` is `0` outside the caller's network.
 */
export const RiskLandscapeMapCountrySchema = z.object({
  countryCode: z.string(),
  countryName: z.string(),
  countryCodeAlpha3: z.string(),
  grossScore: z.number(),
  facilityCount: z.number(),
  companies: z.array(RiskLandscapeMapCompanySchema),
});

export type RiskLandscapeMapCountry = z.infer<
  typeof RiskLandscapeMapCountrySchema
>;
